/**
 * Choir — the Hymn Report ("The Choir Learns", M3.5).
 *
 * Once a day the Choir rewrites its own doctrine from what it learned watching yesterday's
 * raids, and this tab is where it says so, in its own voice. The chips underneath are the
 * honest version: exactly which knobs moved, generated from the doctrine the server is running,
 * never from the prose.
 *
 * No auth: a player deciding whether to deploy tonight should be able to read it cold.
 */

import type { VNode } from 'preact';
import { apiTry } from '../../net/api.js';
import { ListState, Section } from './common.js';
import { useLoader } from './common.js';

/** Mirrors `apps/server/src/routes/public.ts` HymnReportResponse. */
interface HymnReport {
  title?: string;
  body?: string;
  date?: string;
  changes?: string[];
}

export interface ChoirProps {
  nonce: number;
}

/** The report's own date string, shown as the Choir wrote it and only sanity-checked here. */
function reportDate(raw: string | undefined): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > 40) return '';
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function Choir(props: ChoirProps): VNode {
  const loaded = useLoader<HymnReport>(
    () => apiTry<HymnReport>('/api/hymn-report'),
    `hymn-report:${String(props.nonce)}`,
  );

  const report = loaded.data;
  const title = (report?.title ?? '').trim();
  const body = (report?.body ?? '').trim();
  const changes = (report?.changes ?? []).filter((c) => typeof c === 'string' && c.length > 0);
  const date = reportDate(report?.date);
  // A report with no title and nothing changed is a day the Choir sat still.
  const quiet = title.length === 0 && body.length === 0 && changes.length === 0;

  return (
    <div class="hub-pane">
      <Section title="The Hymn Report" hint="What the Choir learned from yesterday">
        <ListState
          loading={loaded.loading}
          offline={!loaded.loading && report === null}
          empty={quiet}
          emptyText="The Choir is quiet today."
        />
        {!loaded.loading && report !== null && !quiet ? (
          <div class="hub-list">
            <article class="hub-card">
              <h3 class="hub-card-title">{title.length > 0 ? title : 'Today, they changed'}</h3>
              {date.length > 0 ? <span class="hub-card-meta">{date}</span> : null}
              {body.length > 0 ? <p class="hub-card-blurb">{body}</p> : null}
              {changes.length > 0 ? (
                <div class="hub-card-foot">
                  <span class="hub-label">What changed</span>
                  {changes.map((c) => (
                    <span key={c} class="hub-tag">
                      {c}
                    </span>
                  ))}
                </div>
              ) : (
                <p class="hub-card-blurb">
                  They wrote it down, but nothing in their doctrine moved.
                </p>
              )}
            </article>
          </div>
        ) : null}
      </Section>
    </div>
  );
}
